/**
 * Meta pixel Advanced Matching helpers.
 *
 * GTM (container GTM-KL9RPN9V) fires the Meta pixel; this module pushes hashed
 * identity + Meta attribution cookies into the dataLayer so the GTM tag can
 * read them as Advanced Matching fields. Email / first name / last name are
 * SHA-256 hashed client-side per Meta's spec. `external_id` is sent as the raw
 * user ID (not hashed) so it can be matched exactly when CAPI is wired up.
 * The `_fbp` and `_fbc` cookies are passed through as-is — Meta requires them
 * unhashed.
 */

export type AdvancedMatchingUser = {
  userId?: string;
  email?: string;
  name?: string;
};

// `Window.dataLayer` is declared globally in `analytics_utils.tsx` (as `any[]`).
// We reuse that ambient declaration here — re-declaring with a narrower type
// triggers a `Subsequent property declarations must have the same type` error
// from interface merging.

async function sha256Hex(input: string): Promise<string> {
  const bytes = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * SHA-256 of the trimmed + lowercased value. Returns undefined for empty input.
 * Meta's Advanced Matching hashing spec requires lowercase, no surrounding whitespace.
 */
export async function sha256Lower(value: string | undefined | null): Promise<string | undefined> {
  if (!value) return undefined;
  const normalized = value.trim().toLowerCase();
  if (!normalized) return undefined;
  return sha256Hex(normalized);
}

/**
 * Read Meta's attribution cookies unchanged. `_fbp` / `_fbc` encode structured
 * values that Meta parses server-side, so they must not be hashed.
 */
export function readFbCookies(): { fbp?: string; fbc?: string } {
  if (typeof document === 'undefined' || !document.cookie) return {};
  const out: { fbp?: string; fbc?: string } = {};
  for (const part of document.cookie.split(';')) {
    const eq = part.indexOf('=');
    if (eq < 0) continue;
    const name = part.slice(0, eq).trim();
    const value = part.slice(eq + 1).trim();
    if (!value) continue;
    if (name === '_fbp') out.fbp = value;
    else if (name === '_fbc') out.fbc = value;
  }
  return out;
}

function splitName(name: string | undefined): { first?: string; last?: string } {
  if (!name) return {};
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return {};
  if (parts.length === 1) return { first: parts[0] };
  return { first: parts[0], last: parts.slice(1).join(' ') };
}

/**
 * Push a `user_identified` event with hashed identity + Meta cookies into the
 * GTM dataLayer. Call once per user-info hydration; extra calls are harmless
 * (GTM just refreshes Advanced Matching from the latest push).
 *
 * `external_id` is the raw userId string — not hashed — so it can be matched
 * exactly against the server-side CAPI payload when that's wired up.
 */
export async function pushAdvancedMatching(user: AdvancedMatchingUser): Promise<void> {
  if (typeof window === 'undefined') return;

  const { first, last } = splitName(user.name);
  const [em, fn, ln] = await Promise.all([
    sha256Lower(user.email),
    sha256Lower(first),
    sha256Lower(last),
  ]);
  const { fbp, fbc } = readFbCookies();

  window.dataLayer = window.dataLayer || [];

  const payload: Record<string, string> = { event: 'user_identified' };
  if (em) payload.em = em;
  if (fn) payload.fn = fn;
  if (ln) payload.ln = ln;
  if (user.userId) payload.external_id = user.userId;
  if (fbp) payload.fbp = fbp;
  if (fbc) payload.fbc = fbc;

  window.dataLayer.push(payload);
}
